// Query parameters that must never leave the app inside telemetry.
//
// `q` is the content search term. Search over a wellness library is health-adjacent by
// nature — "am I depressed", "panic attacks at work" — so it is treated as sensitive
// even though it is not formally PHI. Keeping it out of the URL is not sufficient on its
// own: it still travels as a request parameter, and Sentry records outgoing request URLs
// as breadcrumbs, which would carry it into error reports verbatim.
const SENSITIVE_PARAMS = ['q'];

const REDACTED = '[redacted]';

// Replace sensitive query-parameter values in a URL string, leaving everything else
// (path, other params) intact so the breadcrumb is still useful for debugging.
export const scrubUrlParams = (url: string): string => {
  if (!url || !url.includes('?')) return url;

  try {
    // Relative request URLs are common here, so parse against a throwaway base.
    const parsed = new URL(url, 'http://scrub.local');
    let changed = false;

    SENSITIVE_PARAMS.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, REDACTED);
        changed = true;
      }
    });

    if (!changed) return url;

    return url.startsWith('http') ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    // A URL we can't parse is one we can't prove is safe — drop the query string.
    return url.split('?')[0];
  }
};

// Scrub the places Sentry carries a URL: breadcrumb messages, breadcrumb data, and the
// request on the event itself.
export const scrubSentryBreadcrumb = <T extends { message?: string; data?: Record<string, unknown> }>(
  breadcrumb: T,
): T => {
  if (typeof breadcrumb.message === 'string') breadcrumb.message = scrubUrlParams(breadcrumb.message);

  const url = breadcrumb.data?.url;
  if (typeof url === 'string' && breadcrumb.data) breadcrumb.data.url = scrubUrlParams(url);

  return breadcrumb;
};
